import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React from "react";

interface Props {
  campaigns: any[];
  loading: boolean;
  onScore: (ad: any, idx: number) => void;
  onView: (ad: any, idx: number) => void;
  onClearScore: (ad: any, idx: number) => void;
}

const AdevalGallery = ({ campaigns, loading, onScore, onView, onClearScore }: Props) => {
  if (loading) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Loading campaigns...
      </Typography>
    );
  }

  if (!campaigns.length) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No campaigns found. Check CreativeAI MongoDB connection.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {campaigns.map((ad: any, idx: number) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ position: "relative" }}>
              {ad.bgImageUrl ? (
                <CardMedia
                  component="img"
                  height="180"
                  image={ad.bgImageUrl}
                  alt={ad.campaignName}
                  sx={{ objectFit: "cover" }}
                />
              ) : (
                <Box
                  sx={{
                    height: 180,
                    bgcolor: "grey.800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    No preview
                  </Typography>
                </Box>
              )}
              {ad.lastScore != null && (
                <Chip
                  label={`${ad.lastScore}/10`}
                  size="small"
                  onClick={() => onView(ad, idx)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    fontWeight: "bold",
                    cursor: "pointer",
                    bgcolor:
                      ad.lastScore >= 7
                        ? "#4caf50"
                        : ad.lastScore >= 5
                        ? "#ff9800"
                        : "#f44336",
                    color: "#fff",
                  }}
                />
              )}
            </Box>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" gutterBottom noWrap>
                {ad.campaignName || "Untitled"}
              </Typography>
              {ad.headline && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {ad.headline}
                </Typography>
              )}
              {ad.secondary && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  {ad.secondary}
                </Typography>
              )}
              <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {ad.vertical && (
                  <Chip label={ad.vertical} size="small" variant="outlined" />
                )}
                {ad.template && (
                  <Chip
                    label={ad.template}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                variant="contained"
                onClick={() => onScore(ad, idx)}
                disabled={!ad.bgImageUrl}
              >
                Score This
              </Button>
              {ad.lastScore != null && (
                <>
                  <Button
                    size="small"
                    color="info"
                    onClick={() => onView(ad, idx)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => onClearScore(ad, idx)}
                  >
                    Clear
                  </Button>
                </>
              )}
              {ad.advertiserUrl && (
                <Button
                  size="small"
                  href={ad.advertiserUrl}
                  target="_blank"
                  rel="noopener"
                >
                  Site
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default AdevalGallery;
